import { useSearchParams } from "next/navigation"
import { signIn } from "next-auth/react"
import { FaApple, FaFacebook, FaLinkedin } from "react-icons/fa"
import { FcGoogle } from "react-icons/fc"
import Button from "./Button"
import Link from "next/link"
import { Routes } from "@/lib/routes"
import { cn } from "@/utils/helpers/utils"

const SOCIAL_PROVIDERS = [
   { id: "google", label: "Continue with Google", Icon: FcGoogle, color: "", hidden: false },
   { id: "facebook", label: "Continue with Facebook", Icon: FaFacebook, color: "text-blue-600", hidden: true },
   { id: "linkedin", label: "Continue with LinkedIn", Icon: FaLinkedin, color: "text-blue-700", hidden: true },
   { id: "apple", label: "Continue with Apple", Icon: FaApple, color: "text-black", hidden: true },
] as const

export const SocialLoginSection = ({ page }: { page: "signup" | "login" }) => {
   const searchParams = useSearchParams()
   const redirectUrl = searchParams.get("redirect")

   const handleSignIn = async (provider: "google" | "apple") => {
      const urlCallback = searchParams.get("callbackUrl")
      const resourceName = searchParams.get("resource")

      let targetDestination = ""

      if (urlCallback) {
         targetDestination = decodeURIComponent(urlCallback)
      } else {
         const queryParts = []
         if (redirectUrl) queryParts.push(`redirect=${redirectUrl}`)
         if (resourceName) queryParts.push(`resource=${resourceName}`)

         const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : ""
         targetDestination = `${window.location.pathname}${queryString}`
      }

      await signIn(provider, { callbackUrl: targetDestination })
   }

   return (
      <div className="">
         <div className="mb-3 flex w-full items-center">
            <hr className="flex-grow border-t border-gray-300" style={{ height: "1px" }} />
            <span className="mx-2 text-custom-sm-bold">or</span>
            <hr className="flex-grow border-t border-gray-300" style={{ height: "1px" }} />
         </div>

         <div className="flex justify-center gap-3">
            <div id="social-signup" className=" md:w-full">
               <div className="mt-2 grid justify-items-center gap-3 md:grid-cols-1 md:justify-items-stretch">
                  {SOCIAL_PROVIDERS.map(({ id, label, Icon, color, hidden }) => (
                     <Button
                        key={id}
                        type="button"
                        variant="outline"
                        onClick={() => handleSignIn(id as any)}
                        className={cn(
                           "flex w-full justify-center border-[#7070702B] bg-gradient-to-r from-[#F8F8F8] to-[#f3f3f3] px-4 py-3 text-custom-14-medium hover:bg-gradient-to-r hover:from-[#f3f3f3] hover:to-[#e8e8e8]",
                           hidden && "hidden",
                        )}
                     >
                        <div className="flex items-center gap-2">
                           <Icon className={`ml-2 h-6 w-6 shrink-0 ${color}`} />
                           <span className="truncate">{label}</span>
                        </div>
                     </Button>
                  ))}
               </div>

               <p className="mt-4 text-center text-custom-sm-normal text-[#333333]">
                  {page === "login" ? "Not a member? " : "Already a member? "}
                  <Link
                     href={
                        page === "login"
                           ? redirectUrl
                              ? `${Routes.client.unifiedSignup.getPath()}?redirect=training`
                              : Routes.client.unifiedSignup.getPath()
                           : redirectUrl
                             ? `${Routes.client.unifiedLogin.getPath()}?redirect=training`
                             : Routes.client.unifiedLogin.getPath()
                     }
                     className="leading-0 text-custom-sm-bold text-brandOrange hover:text-brandOrange/70"
                  >
                     {page === "login" ? "Sign up" : "Log in"}
                  </Link>
               </p>
            </div>
         </div>

         <div className="">
            <p className="mt-4 w-full text-center text-custom-sm-normal text-[#333333]">
               <Link href={Routes.client.newsPage.getPath()} className="transition-colors hover:text-orange-500">
                  Industry News & Trends
               </Link>
               {" | "}
               <Link href={Routes.client.resources.getPath()} className="transition-colors hover:text-orange-500">
                  Free Restaurant Resources
               </Link>
               {" | "}
               <Link href={Routes.external.training.getPath()} className="transition-colors hover:text-orange-500">
                  Webinars & Masterclasses
               </Link>
               {" | "}
               <Link href={Routes.client.shows.getPath()} className="transition-colors hover:text-orange-500">
                  Chomp Show Access
               </Link>
            </p>
         </div>
      </div>
   )
}
